import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketplaceCoinIcon from "./MarketplaceCoinIcon";
import MarketplaceManualGrantForm from "./MarketplaceManualGrantForm";
import {
  fetchAdminMarketplaceTransactions,
  fetchMyMarketplaceTransactions,
} from "./marketplaceApi";

const PAGE_SIZE = 50;

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function formatAmount(n) {
  return Math.abs(Math.trunc(Number(n) || 0)).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
}

/**
 * @param {{ refreshKey?: number, mode?: "self" | "admin", onGrantComplete?: () => void }} props
 * mode=self — current user's earn/spend history (default for all shop visitors)
 * mode=admin — all users with optional email filter (admins only)
 */
export default function MarketplaceAdminTransactionHistory({
  refreshKey = 0,
  mode = "self",
  onGrantComplete,
}) {
  const isAdmin = mode === "admin";
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [emailFilter, setEmailFilter] = useState("");
  const [appliedEmailFilter, setAppliedEmailFilter] = useState("");

  const load = useCallback(
    async ({ append = false, nextOffset = 0, email = appliedEmailFilter } = {}) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError("");
      try {
        const data = isAdmin
          ? await fetchAdminMarketplaceTransactions({
              limit: PAGE_SIZE,
              offset: nextOffset,
              userEmail: email,
            })
          : await fetchMyMarketplaceTransactions({
              limit: PAGE_SIZE,
              offset: nextOffset,
            });
        const batch = Array.isArray(data?.transactions) ? data.transactions : [];
        setRows((prev) => (append ? [...prev, ...batch] : batch));
        setTotal(Number(data?.total) || 0);
        setOffset(nextOffset + batch.length);
      } catch (err) {
        if (!append) setRows([]);
        setError(err?.response?.data?.error || err?.message || "Could not load transaction history.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [appliedEmailFilter, isAdmin]
  );

  useEffect(() => {
    load({ append: false, nextOffset: 0, email: appliedEmailFilter });
  }, [load, appliedEmailFilter, refreshKey]);

  const onApplyFilter = (e) => {
    e.preventDefault();
    setAppliedEmailFilter(String(emailFilter || "").trim());
  };

  const onClearFilter = () => {
    setEmailFilter("");
    setAppliedEmailFilter("");
  };

  const hasMore = rows.length < total;

  return (
    <>
      {isAdmin ? (
        <MarketplaceManualGrantForm
          onGranted={() => {
            if (typeof onGrantComplete === "function") onGrantComplete();
            load({ append: false, nextOffset: 0, email: appliedEmailFilter });
          }}
        />
      ) : null}

      <section className="marketplace-admin-history" aria-labelledby="marketplace-admin-history-title">
        <div className="marketplace-admin-history-head">
          <div>
            <h2 id="marketplace-admin-history-title" className="marketplace-admin-history-title">
              {isAdmin ? "All transaction history" : "Your transaction history"}
            </h2>
            <p className="marketplace-admin-history-lead">
              {isAdmin
                ? "Every V-coin reward and shop purchase across all users."
                : "Your V-coin rewards and shop purchases."}
            </p>
          </div>
          {isAdmin ? (
            <form className="marketplace-admin-history-filter" onSubmit={onApplyFilter}>
              <label className="marketplace-admin-history-filter-label">
                <span className="marketplace-admin-history-filter-text">Filter by email</span>
                <input
                  type="search"
                  className="marketplace-admin-history-filter-input"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  placeholder="user@example.com"
                  autoComplete="off"
                />
              </label>
              <button type="submit" className="marketplace-admin-history-filter-btn">
                Apply
              </button>
              {appliedEmailFilter ? (
                <button type="button" className="marketplace-admin-history-filter-clear" onClick={onClearFilter}>
                  Clear
                </button>
              ) : null}
            </form>
          ) : null}
        </div>

        {loading ? <p className="email-triggers-muted">Loading transactions…</p> : null}
        {error ? (
          <p className="user-profile-settings-error" role="alert">
            {error}
          </p>
        ) : null}

        {!loading && !error ? (
          <>
            <p className="marketplace-admin-history-meta">
              Showing {rows.length.toLocaleString()} of {total.toLocaleString()} transactions
              {isAdmin && appliedEmailFilter ? ` matching “${appliedEmailFilter}”` : ""}
            </p>
            {rows.length === 0 ? (
              <p className="email-triggers-muted">No transactions yet.</p>
            ) : (
              <div className="marketplace-admin-history-table-wrap">
                <table className="marketplace-admin-history-table">
                  <thead>
                    <tr>
                      <th scope="col">When</th>
                      {isAdmin ? <th scope="col">User</th> : null}
                      <th scope="col">Type</th>
                      <th scope="col">Details</th>
                      <th scope="col">Project</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Balance after</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isSpend = row.kind === "spend";
                      const note = String(row.note || "").trim();
                      const isManual = row.triggerId === "adminManualGrant";
                      return (
                        <tr key={row.id}>
                          <td className="marketplace-admin-history-when">{formatWhen(row.createdAt)}</td>
                          {isAdmin ? (
                            <td>
                              <div className="marketplace-admin-history-user">
                                <span className="marketplace-admin-history-user-name">{row.userDisplayName}</span>
                                <span className="marketplace-admin-history-user-email">{row.userEmail}</span>
                              </div>
                            </td>
                          ) : null}
                          <td>
                            <span
                              className={`marketplace-admin-history-kind marketplace-admin-history-kind--${row.kind}`}
                            >
                              {isSpend ? "Spend" : "Earn"}
                            </span>
                          </td>
                          <td
                            className="marketplace-admin-history-detail"
                            title={
                              [
                                isManual ? "Manual award" : row.detail,
                                note ? `“${note}”` : "",
                                isAdmin && isManual && row.grantedByDisplayName
                                  ? `by ${row.grantedByDisplayName}`
                                  : "",
                              ]
                                .filter(Boolean)
                                .join(" · ")
                            }
                          >
                            <span className="marketplace-admin-history-detail-main">
                              {isManual ? "Manual award" : row.detail}
                              {note ? ` — “${note}”` : ""}
                              {isAdmin && isManual && row.grantedByDisplayName
                                ? ` · by ${row.grantedByDisplayName}`
                                : ""}
                            </span>
                          </td>
                          <td className="marketplace-admin-history-project">
                            {row.projectId ? (
                              <Link
                                to={`/project/${row.projectId}`}
                                className="marketplace-admin-history-project-link"
                              >
                                {row.projectLabel || `#${row.projectId}`}
                              </Link>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>
                            <span
                              className={`marketplace-admin-history-amount marketplace-admin-history-amount--${row.kind}`}
                            >
                              {isSpend ? "−" : "+"}
                              <MarketplaceCoinIcon size={14} />
                              {formatAmount(row.amount)}
                            </span>
                          </td>
                          <td className="marketplace-admin-history-balance">
                            {row.balanceAfter != null ? (
                              <span className="marketplace-admin-history-balance-pill">
                                <MarketplaceCoinIcon size={14} />
                                {formatAmount(row.balanceAfter)}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {hasMore ? (
              <div className="marketplace-admin-history-more">
                <button
                  type="button"
                  className="marketplace-admin-history-more-btn"
                  disabled={loadingMore}
                  onClick={() => load({ append: true, nextOffset: offset, email: appliedEmailFilter })}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </section>
    </>
  );
}
